import {
  type FireflyTertiaryButtonMenuItem,
  FAIconName,
  FireflyTertiaryButtonMenu,
} from '@equip.health/ui';
import { useState } from 'react';
import { useDocumentUpload } from '~/lib/hooks/usePatientDocumentUpload';
import { useGetAllPatientDocumentsInfiniteQuery } from '~/store/services/documents';
import UploadConfirmationModal from '../profile/common/UploadConfirmationModal';
import type { AttachmentType } from '~/lib/constants/AddPatientNoteModal.constants';
import {
  NOTE_ATTACHMENT_MENU_NODE,
  NOTE_ATTACHMENT_MENU_OPTIONS,
  NOTE_ATTACHMENT_SOURCES,
} from '~/lib/constants/notes';
import AttachDocumentsModal from '../profile/common/AttachDocumentsModal';
import { NoteEvents } from '~/analytics';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

type NoteAttachmentMenuProps = {
  id: string;
  patientId?: string;
  onUploadSuccess?: (docs: AttachmentType[]) => void;
  attachedDocuments?: AttachmentType[];
  source?: string;
  fromEditNoteForm?: boolean;
};

const NoteAttachmentMenu = ({
  id,
  patientId,
  onUploadSuccess,
  attachedDocuments,
  source,
  fromEditNoteForm = false,
}: NoteAttachmentMenuProps) => {
  const { trackEvent } = useAnalytics();
  const [isUploadSuccessModalOpen, setIsUploadSuccessModalOpen] =
    useState(false);
  const [isAttachDocumentsModalOpen, setIsAttachDocumentsModalOpen] =
    useState(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileUploadErrorMessages, setFileUploadErrorMessages] = useState<
    string[]
  >([]);

  const {
    data: documents,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useGetAllPatientDocumentsInfiniteQuery(
    {
      patientId,
      searchTerm,
    },
    { skip: !patientId },
  );
  const { openFileExplorer } = useDocumentUpload({
    onUploadSuccess: (files, modalErrorMessages) => {
      if (files.length === 0) return;
      setUploadedFiles(files);
      setFileUploadErrorMessages(modalErrorMessages);
      setIsUploadSuccessModalOpen(true);
    },
    patientId,
  });
  const hasDocuments = (documents?.pages.at(0)?.data.length ?? 0) > 0;

  const attachmentOptions: FireflyTertiaryButtonMenuItem[] =
    NOTE_ATTACHMENT_MENU_OPTIONS.map((option) => ({
      isDisabledOption:
        option.node === NOTE_ATTACHMENT_MENU_NODE.LINK && !hasDocuments,
      label: option.label,
      leftIcon: option.leftIcon,
      leftIconColor: 'neutral.primary',
      node: option.node,
    }));

  const handleAttachFileOptionsClick = (node?: string) => {
    if (node === NOTE_ATTACHMENT_MENU_NODE.LINK) {
      if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
        trackEvent(NoteEvents.linkFromDocumentOptionClicked, {
          Source: AnalyticsSourceValues.INQUIRY_DETAILS,
          node,
        });
      } else {
        trackEvent(NoteEvents.linkFromDocumentOptionClicked, {
          Source: AnalyticsSourceValues.NOTES_TAB,
          node,
        });
      }
      setIsAttachDocumentsModalOpen(true);
      setSearchTerm('');
    } else if (node === NOTE_ATTACHMENT_MENU_NODE.UPLOAD) {
      if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
        trackEvent(NoteEvents.uploadFileFromComputerOptionClicked, {
          Source: AnalyticsSourceValues.INQUIRY_DETAILS,
          node,
        });
      } else {
        trackEvent(NoteEvents.uploadFileFromComputerOptionClicked, {
          Source: AnalyticsSourceValues.NOTES_TAB,
          node,
        });
      }
      openFileExplorer();
    }
  };

  const handleAttachFileButtonClick = () => {
    if (source === NOTE_ATTACHMENT_SOURCES.INQUIRY_PANEL) {
      trackEvent(NoteEvents.attachFileButtonClicked, {
        Source: AnalyticsSourceValues.INQUIRY_DETAILS,
      });
    } else {
      trackEvent(NoteEvents.attachFileButtonClicked, {
        Source: AnalyticsSourceValues.NOTES_TAB,
      });
    }
  };
  return (
    <>
      <FireflyTertiaryButtonMenu
        buttonId={`${id}__attachment-menu`}
        buttonLeftIcon={FAIconName.paperclipRegular}
        buttonText="Attach file"
        onOptionClick={handleAttachFileOptionsClick}
        options={attachmentOptions}
        onButtonClick={handleAttachFileButtonClick}
      />
      {isUploadSuccessModalOpen && (
        <UploadConfirmationModal
          id={id}
          isOpen={isUploadSuccessModalOpen}
          onClose={() => setIsUploadSuccessModalOpen(false)}
          onUploadSuccess={onUploadSuccess}
          patientId={patientId}
          uploadedFiles={uploadedFiles}
          uploadErrorMessages={fileUploadErrorMessages}
        />
      )}
      {isAttachDocumentsModalOpen && (
        <AttachDocumentsModal
          attachedDocuments={attachedDocuments}
          documents={documents?.pages.flatMap((page) => page.data) ?? []}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          id={id}
          isFetching={isFetching}
          isOpen={isAttachDocumentsModalOpen}
          source={source}
          onClose={() => {
            setSearchTerm('');
            setIsAttachDocumentsModalOpen(false);
          }}
          onUploadSuccess={fromEditNoteForm ? onUploadSuccess : undefined}
          setSearchTerm={setSearchTerm}
        />
      )}
    </>
  );
};

export default NoteAttachmentMenu;
